import { type CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import { reportImageLoadFailure } from '@/api/telemetry';

export type Rotation = 0 | 90 | 180 | 270;

/** Next quarter turn; `dir` 1 = clockwise, -1 = counter-clockwise. */
export function turn(rotation: Rotation, dir: 1 | -1): Rotation {
  return ((((rotation + dir * 90) % 360) + 360) % 360) as Rotation;
}

/**
 * A photo that fills its parent (letterboxed, never cropped — UI_GUIDELINES §6)
 * and can be shown rotated in 90° steps. Rotation is a viewing aid for the
 * judge only; nothing is written back to the image.
 *
 * `object-fit: contain` cannot letterbox a rotated image (the box stays
 * upright while the pixels turn), so the parent is measured and the image's
 * max size is swapped when it lies sideways: its width is bounded by the
 * parent's height and vice versa. The browser then preserves the aspect
 * ratio inside those bounds and the rotated result fits exactly.
 */
export function RotatablePhoto({
  src,
  alt,
  rotation,
  onClick,
  imgClassName = '',
}: {
  src: string;
  alt: string;
  rotation: Rotation;
  onClick?: () => void;
  imgClassName?: string;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => setFrame({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const sideways = rotation === 90 || rotation === 270;
  const measured = frame.w > 0 && frame.h > 0;
  const style: CSSProperties = {
    maxWidth: measured ? (sideways ? frame.h : frame.w) : '100%',
    maxHeight: measured ? (sideways ? frame.w : frame.h) : '100%',
    transform: rotation === 0 ? undefined : `rotate(${rotation}deg)`,
  };

  return (
    <div ref={frameRef} className="flex h-full w-full items-center justify-center overflow-hidden">
      <img
        src={src}
        alt={alt}
        onClick={onClick}
        onError={() => reportImageLoadFailure('full', src)}
        draggable={false}
        style={style}
        className={`block h-auto w-auto select-none ${imgClassName}`}
      />
    </div>
  );
}
